import React, { Component } from 'react';
import { connect } from 'react-redux';
import { jsPDF } from 'jspdf';
import rfqService from '../../services/rfqService';
import { updateActiveProject } from '../../actions/projectListActions';
import { updateActiveTabIndex } from '../../actions/uiFlagsActions';
import './RfqManager.css';

class RfqManager extends Component {
    constructor(props) {
        super(props);
        this.state = {
            rfqs: [],
            loading: true,
            error: null,
            showDetailsModal: false,
            selectedRfq: null,
            sortColumn: 'createdAt',
            sortDirection: 'desc',
            modalPosition: { x: 0, y: 0 },
            modalSize: { width: 800, height: 600 },
            isDragging: false,
            isResizing: false,
            dragOffset: { x: 0, y: 0 },
            activeTab: 'details',
            selectedIds: new Set(),
            deleting: false
        };
        this.handleMouseDown = this.handleMouseDown.bind(this);
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleMouseUp = this.handleMouseUp.bind(this);
        this.handleResizeMouseDown = this.handleResizeMouseDown.bind(this);
    }

    componentDidMount() {
        this.loadRfqs();
        document.addEventListener('mousemove', this.handleMouseMove);
        document.addEventListener('mouseup', this.handleMouseUp);
    }

    componentWillUnmount() {
        document.removeEventListener('mousemove', this.handleMouseMove);
        document.removeEventListener('mouseup', this.handleMouseUp);
    }

    handleMouseDown(e) {
        var modal = document.getElementById('rfq-draggable-modal');
        if (modal) {
            var rect = modal.getBoundingClientRect();
            this.setState({
                isDragging: true,
                dragOffset: {
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top
                }
            });
        }
    }

    handleResizeMouseDown(e) {
        e.stopPropagation();
        var modal = document.getElementById('rfq-draggable-modal');
        if (modal) {
            var rect = modal.getBoundingClientRect();
            this.setState({
                isResizing: true,
                dragOffset: {
                    x: e.clientX,
                    y: e.clientY,
                    width: rect.width,
                    height: rect.height
                }
            });
        }
    }

    handleMouseMove(e) {
        if (this.state.isDragging) {
            this.setState({
                modalPosition: {
                    x: e.clientX - this.state.dragOffset.x,
                    y: e.clientY - this.state.dragOffset.y
                }
            });
        } else if (this.state.isResizing) {
            var newWidth = this.state.dragOffset.width + (e.clientX - this.state.dragOffset.x);
            var newHeight = this.state.dragOffset.height + (e.clientY - this.state.dragOffset.y);
            this.setState({
                modalSize: {
                    width: Math.max(500, newWidth),
                    height: Math.max(400, newHeight)
                }
            });
        }
    }

    handleMouseUp() {
        this.setState({ isDragging: false, isResizing: false });
    }

    async loadRfqs() {
        try {
            var rfqs = await rfqService.getAll();
            this.setState({ rfqs: rfqs, loading: false });
        } catch (err) {
            this.setState({ error: err.message, loading: false });
        }
    }

    handleSort(column) {
        var newDirection = 'asc';
        if (this.state.sortColumn === column && this.state.sortDirection === 'asc') {
            newDirection = 'desc';
        }
        this.setState({ sortColumn: column, sortDirection: newDirection });
    }

    getSortedRfqs() {
        var rfqs = this.state.rfqs.slice();
        var column = this.state.sortColumn;
        var direction = this.state.sortDirection;
        var self = this;

        rfqs.sort(function(a, b) {
            var aVal, bVal;

            switch (column) {
                case 'referenceNumber':
                    aVal = (a.referenceNumber || '').toLowerCase();
                    bVal = (b.referenceNumber || '').toLowerCase();
                    break;
                case 'productName':
                    aVal = (a.productName || '').toLowerCase();
                    bVal = (b.productName || '').toLowerCase();
                    break;
                case 'companyName':
                    aVal = (a.companyName || '').toLowerCase();
                    bVal = (b.companyName || '').toLowerCase();
                    break;
                case 'contact':
                    aVal = ((a.customerFirstName || '') + ' ' + (a.customerLastName || '')).toLowerCase();
                    bVal = ((b.customerFirstName || '') + ' ' + (b.customerLastName || '')).toLowerCase();
                    break;
                case 'customerEmail':
                    aVal = (a.customerEmail || '').toLowerCase();
                    bVal = (b.customerEmail || '').toLowerCase();
                    break;
                case 'status':
                    aVal = (a.status || '').toLowerCase();
                    bVal = (b.status || '').toLowerCase();
                    break;
                case 'quotedPrice':
                    aVal = parseFloat(a.quotedPrice) || 0;
                    bVal = parseFloat(b.quotedPrice) || 0;
                    break;
                case 'createdAt':
                    aVal = new Date(a.createdAt).getTime();
                    bVal = new Date(b.createdAt).getTime();
                    break;
                default:
                    aVal = (a[column] || '').toString().toLowerCase();
                    bVal = (b[column] || '').toString().toLowerCase();
            }

            if (aVal < bVal) return direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return direction === 'asc' ? 1 : -1;
            return 0;
        });

        return rfqs;
    }

    getSortIndicator(column) {
        if (this.state.sortColumn !== column) return ' ↕';
        return this.state.sortDirection === 'asc' ? ' ▲' : ' ▼';
    }

    openDetailsModal(rfq) {
        this.setState({
            showDetailsModal: true,
            selectedRfq: rfq,
            modalPosition: { x: 0, y: 0 },
            modalSize: { width: 800, height: 600 },
            activeTab: 'details'
        });
    }

    closeDetailsModal() {
        this.setState({ showDetailsModal: false, selectedRfq: null });
    }

    setActiveTab(tab) {
        this.setState({ activeTab: tab });
    }

    async updateStatus(rfqId, newStatus) {
        try {
            await rfqService.update(rfqId, { status: newStatus });
            this.loadRfqs();
            var updatedRfq = Object.assign({}, this.state.selectedRfq, { status: newStatus });
            this.setState({ selectedRfq: updatedRfq });
        } catch (err) {
            alert('Failed to update status: ' + err.message);
        }
    }

    async saveNotes(rfqId, notes) {
        try {
            await rfqService.update(rfqId, { notes: notes });
            this.loadRfqs();
            var updatedRfq = Object.assign({}, this.state.selectedRfq, { notes: notes });
            this.setState({ selectedRfq: updatedRfq });
            alert('Notes saved!');
        } catch (err) {
            alert('Failed to save notes: ' + err.message);
        }
    }

    async saveQuote(rfqId) {
        var rfq = this.state.selectedRfq;
        try {
            await rfqService.update(rfqId, {
                quotedPrice: rfq.quotedPrice,
                quotedBy: rfq.quotedBy,
                quotedByEmail: rfq.quotedByEmail,
                quotedByPhone: rfq.quotedByPhone,
                quoteValidUntil: rfq.quoteValidUntil,
                quotedAt: new Date().toISOString(),
                status: 'quoted'
            });
            this.loadRfqs();
            var updatedRfq = Object.assign({}, rfq, { 
                quotedAt: new Date().toISOString(),
                status: 'quoted'
            });
            this.setState({ selectedRfq: updatedRfq });
            alert('Quote saved!');
        } catch (err) {
            alert('Failed to save quote: ' + err.message);
        }
    }

    updateSelectedRfq(field, value) {
        var updatedRfq = Object.assign({}, this.state.selectedRfq);
        updatedRfq[field] = value;
        this.setState({ selectedRfq: updatedRfq });
    }

    async deleteRfq(id) {
        if (!window.confirm('Are you sure you want to delete this RFQ?')) {
            return;
        }
        try {
            await rfqService.delete(id);
            this.closeDetailsModal();
            this.loadRfqs();
        } catch (err) {
            alert('Failed to delete RFQ: ' + err.message);
        }
    }

    toggleSelectItem(id) {
        this.setState(function(prevState) {
            var newSet = new Set(prevState.selectedIds);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return { selectedIds: newSet };
        });
    }

    toggleSelectAll() {
        var self = this;
        this.setState(function(prevState) {
            var allIds = prevState.rfqs.map(function(rfq) { return rfq.id; });
            var allSelected = allIds.length > 0 && allIds.every(function(id) { return prevState.selectedIds.has(id); });
            if (allSelected) {
                return { selectedIds: new Set() };
            } else {
                return { selectedIds: new Set(allIds) };
            }
        });
    }

    async deleteSelected() {
        var count = this.state.selectedIds.size;
        if (count === 0) return;

        if (!window.confirm('Are you sure you want to delete ' + count + ' selected RFQ(s)?')) {
            return;
        }

        this.setState({ deleting: true });
        try {
            var ids = Array.from(this.state.selectedIds);
            await rfqService.deleteMultiple(ids);
            this.setState({ selectedIds: new Set() });
            this.loadRfqs();
        } catch (err) {
            alert('Failed to delete RFQs: ' + err.message);
        } finally {
            this.setState({ deleting: false });
        }
    }

    formatDate(dateString) {
        if (!dateString) return '-';
        var date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    }

    formatDateShort(dateString) {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    }

    formatCurrency(amount) {
        if (!amount) return '-';
        return '$' + parseFloat(amount).toFixed(2);
    }

    parseConfiguration(jsonString) {
        if (!jsonString) return [];
        try {
            var config = JSON.parse(jsonString);
            var params = [];
            for (var key in config) {
                if (config.hasOwnProperty(key)) {
                    params.push({
                        name: key,
                        value: config[key]
                    });
                }
            }
            return params;
        } catch (e) {
            return [];
        }
    }

    exportToCsv() {
        var rfqs = this.getSortedRfqs();
        if (rfqs.length === 0) return;

        var headers = ['Reference', 'Product', 'Company', 'Name', 'Email', 'Phone', 'Status', 'Quoted Price', 'Date'];
        var self = this;
        var rows = rfqs.map(function(rfq) {
            return [
                rfq.referenceNumber,
                rfq.productName,
                rfq.companyName,
                rfq.customerFirstName + ' ' + rfq.customerLastName,
                rfq.customerEmail,
                rfq.customerPhone,
                rfq.status,
                rfq.quotedPrice || '',
                self.formatDateShort(rfq.createdAt)
            ];
        });

        var csvContent = [headers].concat(rows).map(function(row) { return row.join(','); }).join('\n');
        var blob = new Blob([csvContent], { type: 'text/csv' });
        var url = window.URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = 'rfq-export.csv';
        a.click();
        window.URL.revokeObjectURL(url);
    }

    downloadConfigJson() {
        var rfq = this.state.selectedRfq;
        if (!rfq || !rfq.configurationJson) return;
        
        var blob = new Blob([rfq.configurationJson], { type: 'application/json' });
        var url = window.URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = rfq.referenceNumber + '-config.json';
        a.click();
        window.URL.revokeObjectURL(url);
    }

    generateQuotePdf() {
        var rfq = this.state.selectedRfq;
        if (!rfq) return;

        var doc = new jsPDF();
        var config = this.parseConfiguration(rfq.configurationJson);
        var yPos = 20;

        // Header
        doc.setFontSize(24);
        doc.setTextColor(217, 115, 13);
        doc.text('QUOTE', 105, yPos, { align: 'center' });
        yPos += 15;

        // Reference number
        doc.setFontSize(12);
        doc.setTextColor(100);
        doc.text('Reference: ' + rfq.referenceNumber, 105, yPos, { align: 'center' });
        yPos += 5;

        // Line
        doc.setDrawColor(217, 115, 13);
        doc.setLineWidth(0.5);
        doc.line(20, yPos, 190, yPos);
        yPos += 15;

        // Customer Information
        doc.setFontSize(14);
        doc.setTextColor(217, 115, 13);
        doc.text('Customer Information', 20, yPos);
        yPos += 8;

        doc.setFontSize(11);
        doc.setTextColor(0);
        doc.text(rfq.customerFirstName + ' ' + rfq.customerLastName, 20, yPos);
        yPos += 6;
        if (rfq.companyName) {
            doc.text(rfq.companyName, 20, yPos);
            yPos += 6;
        }
        doc.text(rfq.customerEmail, 20, yPos);
        yPos += 6;
        if (rfq.customerPhone) {
            doc.text(rfq.customerPhone, 20, yPos);
            yPos += 6;
        }
        if (rfq.zipPostalCode || rfq.country) {
            doc.text((rfq.zipPostalCode || '') + ' ' + (rfq.country || ''), 20, yPos);
            yPos += 6;
        }
        yPos += 10;

        // Product Configuration
        doc.setFontSize(14);
        doc.setTextColor(217, 115, 13);
        doc.text('Product: ' + rfq.productName, 20, yPos);
        yPos += 10;

        if (config.length > 0) {
            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text('Parameter', 20, yPos);
            doc.text('Value', 100, yPos);
            yPos += 2;
            doc.line(20, yPos, 190, yPos);
            yPos += 6;

            doc.setTextColor(0);
            for (var i = 0; i < config.length; i++) {
                doc.text(config[i].name, 20, yPos);
                doc.text(String(config[i].value), 100, yPos);
                yPos += 7;
                if (yPos > 250) {
                    doc.addPage();
                    yPos = 20;
                }
            }
        }
        yPos += 10;

        // Quoted Price
        if (rfq.quotedPrice) {
            doc.setFontSize(14);
            doc.setTextColor(217, 115, 13);
            doc.text('Quoted Price', 20, yPos);
            yPos += 10;

            doc.setFontSize(20);
            doc.setTextColor(40, 167, 69);
            doc.text('$' + parseFloat(rfq.quotedPrice).toFixed(2), 20, yPos);
            yPos += 10;

            if (rfq.quoteValidUntil) {
                doc.setFontSize(10);
                doc.setTextColor(100);
                doc.text('Valid until: ' + new Date(rfq.quoteValidUntil).toLocaleDateString(), 20, yPos);
                yPos += 6;
            }
            yPos += 10;
        }

        // Special Requests
        if (rfq.specialRequests) {
            doc.setFontSize(14);
            doc.setTextColor(217, 115, 13);
            doc.text('Special Requests', 20, yPos);
            yPos += 8;

            doc.setFontSize(11);
            doc.setTextColor(0);
            var splitRequests = doc.splitTextToSize(rfq.specialRequests, 170);
            doc.text(splitRequests, 20, yPos);
            yPos += splitRequests.length * 6 + 10;
        }

        // Contact Information (Quoter)
        if (rfq.quotedBy || rfq.quotedByEmail || rfq.quotedByPhone) {
            if (yPos > 240) {
                doc.addPage();
                yPos = 20;
            }
            doc.setFontSize(14);
            doc.setTextColor(217, 115, 13);
            doc.text('Contact for Questions', 20, yPos);
            yPos += 8;

            doc.setFontSize(11);
            doc.setTextColor(0);
            if (rfq.quotedBy) {
                doc.text(rfq.quotedBy, 20, yPos);
                yPos += 6;
            }
            if (rfq.quotedByEmail) {
                doc.text('Email: ' + rfq.quotedByEmail, 20, yPos);
                yPos += 6;
            }
            if (rfq.quotedByPhone) {
                doc.text('Phone: ' + rfq.quotedByPhone, 20, yPos);
                yPos += 6;
            }
        }

        // Footer
        doc.setFontSize(9);
        doc.setTextColor(150);
        doc.text('Generated on ' + new Date().toLocaleDateString(), 105, 285, { align: 'center' });

        // Open PDF in new window for print
        var pdfBlob = doc.output('blob');
        var pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, '_blank');
    }

    sendEmail() {
        var rfq = this.state.selectedRfq;
        if (!rfq) return;

        var subject = encodeURIComponent('Quote ' + rfq.referenceNumber + ' - ' + rfq.productName);
        var body = encodeURIComponent(
            'Dear ' + rfq.customerFirstName + ',\n\n' +
            'Thank you for your quote request for ' + rfq.productName + '.\n\n' +
            'Reference Number: ' + rfq.referenceNumber + '\n' +
            (rfq.quotedPrice ? 'Quoted Price: $' + parseFloat(rfq.quotedPrice).toFixed(2) + '\n' : '') +
            (rfq.quoteValidUntil ? 'Valid Until: ' + new Date(rfq.quoteValidUntil).toLocaleDateString() + '\n' : '') +
            '\nPlease let us know if you have any questions.\n\n' +
            'Best regards,\n' +
            (rfq.quotedBy ? rfq.quotedBy + '\n' : '') +
            (rfq.quotedByEmail ? rfq.quotedByEmail + '\n' : '') +
            (rfq.quotedByPhone ? rfq.quotedByPhone : '')
        );

        window.location.href = 'mailto:' + rfq.customerEmail + '?subject=' + subject + '&body=' + body;
    }

    viewModel() {
        var rfq = this.state.selectedRfq;
        if (!rfq) return;
        
        this.closeDetailsModal();
        this.props.updateActiveProject(rfq.productName);
        this.props.updateActiveTabIndex(1);
    }

    getStatusColor(status) {
        switch (status) {
            case 'pending': return '#f0ad4e';
            case 'reviewed': return '#5bc0de';
            case 'quoted': return '#5cb85c';
            case 'accepted': return '#28a745';
            case 'rejected': return '#d9534f';
            case 'expired': return '#999';
            default: return '#666';
        }
    }

    render() {
        var state = this.state;
        var self = this;

        if (state.loading) {
            return <div className="rfq-manager"><p>Loading...</p></div>;
        }

        if (state.error) {
            return <div className="rfq-manager"><p className="error">Error: {state.error}</p></div>;
        }

        var modalStyle = {
            width: state.modalSize.width + 'px',
            height: state.modalSize.height + 'px'
        };
        if (state.modalPosition.x !== 0 || state.modalPosition.y !== 0) {
            modalStyle.position = 'fixed';
            modalStyle.left = state.modalPosition.x + 'px';
            modalStyle.top = state.modalPosition.y + 'px';
            modalStyle.margin = 0;
        }

        var configParams = state.selectedRfq ? this.parseConfiguration(state.selectedRfq.configurationJson) : [];
        var sortedRfqs = this.getSortedRfqs();

        return (
            <div className="rfq-manager">
                <div className="rfq-manager-header">
                    <h2>RFQ Manager</h2>
                    <div className="rfq-manager-actions">
                        {state.selectedIds.size > 0 && (
                            <button
                                onClick={function() { self.deleteSelected(); }}
                                className="btn-delete-selected"
                                disabled={state.deleting}
                            >
                                {state.deleting ? 'Deleting...' : 'Delete Selected (' + state.selectedIds.size + ')'}
                            </button>
                        )}
                        <button onClick={function() { self.loadRfqs(); }} className="btn-refresh">
                            Refresh
                        </button>
                        <button onClick={function() { self.exportToCsv(); }} className="btn-export">
                            Export CSV
                        </button>
                    </div>
                </div>

                <div className="rfq-count">{state.rfqs.length} Quote Request(s)</div>

                {state.rfqs.length === 0 ? (
                    <p className="no-rfqs">No quote requests yet.</p>
                ) : (
                    <table className="rfq-table">
                        <thead>
                            <tr>
                                <th className="rfq-checkbox-col">
                                    <input
                                        type="checkbox"
                                        checked={state.rfqs.length > 0 && state.rfqs.every(function(rfq) { return state.selectedIds.has(rfq.id); })}
                                        onChange={function() { self.toggleSelectAll(); }}
                                        title="Select all"
                                    />
                                </th>
                                <th className="sortable" onClick={function() { self.handleSort('referenceNumber'); }}>
                                    Reference{self.getSortIndicator('referenceNumber')}
                                </th>
                                <th className="sortable" onClick={function() { self.handleSort('productName'); }}>
                                    Product{self.getSortIndicator('productName')}
                                </th>
                                <th className="sortable" onClick={function() { self.handleSort('companyName'); }}>
                                    Company{self.getSortIndicator('companyName')}
                                </th>
                                <th className="sortable" onClick={function() { self.handleSort('contact'); }}>
                                    Contact{self.getSortIndicator('contact')}
                                </th>
                                <th className="sortable" onClick={function() { self.handleSort('customerEmail'); }}>
                                    Email{self.getSortIndicator('customerEmail')}
                                </th>
                                <th className="sortable" onClick={function() { self.handleSort('status'); }}>
                                    Status{self.getSortIndicator('status')}
                                </th>
                                <th className="sortable" onClick={function() { self.handleSort('quotedPrice'); }}>
                                    Price{self.getSortIndicator('quotedPrice')}
                                </th>
                                <th className="sortable" onClick={function() { self.handleSort('createdAt'); }}>
                                    Date{self.getSortIndicator('createdAt')}
                                </th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRfqs.map(function(rfq) {
                                return (
                                    <tr key={rfq.id} className={state.selectedIds.has(rfq.id) ? 'rfq-row-selected' : ''}>
                                        <td className="rfq-checkbox-col">
                                            <input
                                                type="checkbox"
                                                checked={state.selectedIds.has(rfq.id)}
                                                onChange={function() { self.toggleSelectItem(rfq.id); }}
                                            />
                                        </td>
                                        <td>
                                            <span
                                                className="rfq-ref-link"
                                                onClick={function() { self.openDetailsModal(rfq); }}
                                            >
                                                {rfq.referenceNumber}
                                            </span>
                                        </td>
                                        <td>
                                            {rfq.productName}
                                            {rfq.isMultiItem && (
                                                <span className="rfq-multi-item-badge">{rfq.itemCount} items</span>
                                            )}
                                        </td>
                                        <td>{rfq.companyName}</td>
                                        <td>{rfq.customerFirstName} {rfq.customerLastName}</td>
                                        <td><a href={'mailto:' + rfq.customerEmail}>{rfq.customerEmail}</a></td>
                                        <td>
                                            <span 
                                                className="rfq-status-badge"
                                                style={{ backgroundColor: self.getStatusColor(rfq.status) }}
                                            >
                                                {rfq.status}
                                            </span>
                                        </td>
                                        <td>{self.formatCurrency(rfq.quotedPrice)}</td>
                                        <td>{self.formatDateShort(rfq.createdAt)}</td>
                                        <td>
                                            <button 
                                                onClick={function() { self.openDetailsModal(rfq); }}
                                                className="btn-view"
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}

                {/* RFQ Details Modal */}
                {state.showDetailsModal && state.selectedRfq && (
                    <div className="rfq-modal-overlay">
                        <div 
                            id="rfq-draggable-modal"
                            className="rfq-modal rfq-modal-large" 
                            style={modalStyle}
                        >
                            <div 
                                className="rfq-modal-header"
                                onMouseDown={this.handleMouseDown}
                            >
                                <h3>RFQ: {state.selectedRfq.referenceNumber}</h3>
                                <button className="rfq-modal-close" onClick={function() { self.closeDetailsModal(); }}>&times;</button>
                            </div>

                            {/* Quick Actions Bar */}
                            <div className="rfq-quick-actions">
                                <button onClick={function() { self.sendEmail(); }} className="btn-action btn-email">
                                    📧 Email Customer
                                </button>
                                <button onClick={function() { self.viewModel(); }} className="btn-action btn-model">
                                    🔍 View 3D Model
                                </button>
                                <button onClick={function() { self.generateQuotePdf(); }} className="btn-action btn-pdf">
                                    📄 Print Quote
                                </button>
                                <button onClick={function() { self.downloadConfigJson(); }} className="btn-action btn-download">
                                    ⬇️ Download Config
                                </button>
                            </div>

                            {/* Tabs */}
                            <div className="rfq-tabs">
                                <button
                                    className={'rfq-tab ' + (state.activeTab === 'details' ? 'active' : '')}
                                    onClick={function() { self.setActiveTab('details'); }}
                                >
                                    Details
                                </button>
                                {state.selectedRfq.isMultiItem && (
                                    <button
                                        className={'rfq-tab ' + (state.activeTab === 'items' ? 'active' : '')}
                                        onClick={function() { self.setActiveTab('items'); }}
                                    >
                                        Items ({state.selectedRfq.itemCount})
                                    </button>
                                )}
                                {!state.selectedRfq.isMultiItem && (
                                    <button
                                        className={'rfq-tab ' + (state.activeTab === 'config' ? 'active' : '')}
                                        onClick={function() { self.setActiveTab('config'); }}
                                    >
                                        Configuration
                                    </button>
                                )}
                                <button
                                    className={'rfq-tab ' + (state.activeTab === 'quote' ? 'active' : '')}
                                    onClick={function() { self.setActiveTab('quote'); }}
                                >
                                    Quote & Pricing
                                </button>
                                <button
                                    className={'rfq-tab ' + (state.activeTab === 'notes' ? 'active' : '')}
                                    onClick={function() { self.setActiveTab('notes'); }}
                                >
                                    Notes
                                </button>
                            </div>

                            <div className="rfq-modal-body">
                                {/* Details Tab */}
                                {state.activeTab === 'details' && (
                                    <div className="rfq-tab-content">
                                        <div className="rfq-details-grid">
                                            <div className="rfq-details-section">
                                                <h4>Reference Information</h4>
                                                <div className="rfq-detail-row">
                                                    <span className="rfq-detail-label">Reference #:</span>
                                                    <span className="rfq-detail-value">{state.selectedRfq.referenceNumber}</span>
                                                </div>
                                                <div className="rfq-detail-row">
                                                    <span className="rfq-detail-label">Product:</span>
                                                    <span className="rfq-detail-value">
                                                        {state.selectedRfq.productName}
                                                        {state.selectedRfq.isMultiItem && (
                                                            <span className="rfq-multi-item-badge">Multi-Item</span>
                                                        )}
                                                    </span>
                                                </div>
                                                {state.selectedRfq.isMultiItem && (
                                                    <div className="rfq-detail-row">
                                                        <span className="rfq-detail-label">Item Count:</span>
                                                        <span className="rfq-detail-value">{state.selectedRfq.itemCount} items</span>
                                                    </div>
                                                )}
                                                <div className="rfq-detail-row">
                                                    <span className="rfq-detail-label">Status:</span>
                                                    <select
                                                        value={state.selectedRfq.status}
                                                        onChange={function(e) { self.updateStatus(state.selectedRfq.id, e.target.value); }}
                                                        className="rfq-status-select"
                                                    >
                                                        <option value="pending">Pending</option>
                                                        <option value="reviewed">Reviewed</option>
                                                        <option value="quoted">Quoted</option>
                                                        <option value="accepted">Accepted</option>
                                                        <option value="rejected">Rejected</option>
                                                        <option value="expired">Expired</option>
                                                    </select>
                                                </div>
                                                <div className="rfq-detail-row">
                                                    <span className="rfq-detail-label">Created:</span>
                                                    <span className="rfq-detail-value">{self.formatDate(state.selectedRfq.createdAt)}</span>
                                                </div>
                                                <div className="rfq-detail-row">
                                                    <span className="rfq-detail-label">Expires:</span>
                                                    <span className="rfq-detail-value">{self.formatDateShort(state.selectedRfq.expiresAt)}</span>
                                                </div>
                                            </div>

                                            <div className="rfq-details-section">
                                                <h4>Customer Information</h4>
                                                <div className="rfq-detail-row">
                                                    <span className="rfq-detail-label">Name:</span>
                                                    <span className="rfq-detail-value">{state.selectedRfq.customerFirstName} {state.selectedRfq.customerLastName}</span>
                                                </div>
                                                <div className="rfq-detail-row">
                                                    <span className="rfq-detail-label">Company:</span>
                                                    <span className="rfq-detail-value">{state.selectedRfq.companyName || '-'}</span>
                                                </div>
                                                <div className="rfq-detail-row">
                                                    <span className="rfq-detail-label">Email:</span>
                                                    <span className="rfq-detail-value">
                                                        <a href={'mailto:' + state.selectedRfq.customerEmail}>{state.selectedRfq.customerEmail}</a>
                                                    </span>
                                                </div>
                                                <div className="rfq-detail-row">
                                                    <span className="rfq-detail-label">Phone:</span>
                                                    <span className="rfq-detail-value">{state.selectedRfq.customerPhone || '-'}</span>
                                                </div>
                                                <div className="rfq-detail-row">
                                                    <span className="rfq-detail-label">Location:</span>
                                                    <span className="rfq-detail-value">{state.selectedRfq.zipPostalCode} {state.selectedRfq.country}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {state.selectedRfq.specialRequests && (
                                            <div className="rfq-details-section rfq-full-width">
                                                <h4>Special Requests</h4>
                                                <p className="rfq-special-requests">{state.selectedRfq.specialRequests}</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Items Tab (for multi-item RFQs) */}
                                {state.activeTab === 'items' && state.selectedRfq.isMultiItem && (
                                    <div className="rfq-tab-content">
                                        <div className="rfq-details-section">
                                            <h4>Quote Items</h4>
                                            {state.selectedRfq.items && state.selectedRfq.items.length > 0 ? (
                                                <table className="rfq-items-table">
                                                    <thead>
                                                        <tr>
                                                            <th>#</th>
                                                            <th>Product</th>
                                                            <th>Qty</th>
                                                            <th>Configuration</th>
                                                            <th>Notes</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {state.selectedRfq.items.map(function(item) {
                                                            var itemConfig = self.parseConfiguration(item.configurationJson);
                                                            return (
                                                                <tr key={item.id}>
                                                                    <td>{item.lineNumber}</td>
                                                                    <td>
                                                                        <div className="rfq-item-product">
                                                                            {item.thumbnailUrl && (
                                                                                <img src={item.thumbnailUrl} alt="" className="rfq-item-thumbnail" />
                                                                            )}
                                                                            <span>{item.productDisplayName || item.productName}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td>{item.quantity}</td>
                                                                    <td>
                                                                        <div className="rfq-item-config">
                                                                            {itemConfig.slice(0, 3).map(function(param, idx) {
                                                                                return (
                                                                                    <span key={idx} className="rfq-config-tag">
                                                                                        {param.name}: {String(param.value)}
                                                                                    </span>
                                                                                );
                                                                            })}
                                                                            {itemConfig.length > 3 && (
                                                                                <span className="rfq-config-more">+{itemConfig.length - 3} more</span>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td>{item.notes || '-'}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <p>No items available.</p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Configuration Tab */}
                                {state.activeTab === 'config' && (
                                    <div className="rfq-tab-content">
                                        <div className="rfq-details-section">
                                            <h4>Product Configuration</h4>
                                            {configParams.length > 0 ? (
                                                <table className="rfq-config-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Parameter</th>
                                                            <th>Value</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {configParams.map(function(param, index) {
                                                            return (
                                                                <tr key={index}>
                                                                    <td>{param.name}</td>
                                                                    <td>{String(param.value)}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <p>No configuration data available.</p>
                                            )}
                                        </div>
                                        <div className="rfq-config-actions">
                                            <button onClick={function() { self.downloadConfigJson(); }} className="btn-download-config">
                                                Download Configuration JSON
                                            </button>
                                            <button onClick={function() { self.viewModel(); }} className="btn-view-model">
                                                Open in 3D Viewer
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Quote & Pricing Tab */}
                                {state.activeTab === 'quote' && (
                                    <div className="rfq-tab-content">
                                        <div className="rfq-details-section">
                                            <h4>Pricing</h4>
                                            <div className="rfq-form-row">
                                                <div className="rfq-form-group">
                                                    <label>Quoted Price ($)</label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={state.selectedRfq.quotedPrice || ''}
                                                        onChange={function(e) { self.updateSelectedRfq('quotedPrice', e.target.value); }}
                                                        placeholder="0.00"
                                                        className="rfq-price-input"
                                                    />
                                                </div>
                                                <div className="rfq-form-group">
                                                    <label>Quote Valid Until</label>
                                                    <input
                                                        type="date"
                                                        value={state.selectedRfq.quoteValidUntil ? state.selectedRfq.quoteValidUntil.split('T')[0] : ''}
                                                        onChange={function(e) { self.updateSelectedRfq('quoteValidUntil', e.target.value); }}
                                                        className="rfq-date-input"
                                                    />
                                                </div>
                                            </div>
                                            
                                            <h4 style={{marginTop: '20px'}}>Quoted By (Contact for Questions)</h4>
                                            <div className="rfq-form-row">
                                                <div className="rfq-form-group">
                                                    <label>Name</label>
                                                    <input
                                                        type="text"
                                                        value={state.selectedRfq.quotedBy || ''}
                                                        onChange={function(e) { self.updateSelectedRfq('quotedBy', e.target.value); }}
                                                        placeholder="Your name"
                                                        className="rfq-text-input"
                                                    />
                                                </div>
                                                <div className="rfq-form-group">
                                                    <label>Quoted At</label>
                                                    <input
                                                        type="text"
                                                        value={self.formatDate(state.selectedRfq.quotedAt)}
                                                        disabled
                                                        className="rfq-text-input disabled"
                                                    />
                                                </div>
                                            </div>
                                            <div className="rfq-form-row">
                                                <div className="rfq-form-group">
                                                    <label>Email</label>
                                                    <input
                                                        type="email"
                                                        value={state.selectedRfq.quotedByEmail || ''}
                                                        onChange={function(e) { self.updateSelectedRfq('quotedByEmail', e.target.value); }}
                                                        placeholder="your.email@company.com"
                                                        className="rfq-text-input"
                                                    />
                                                </div>
                                                <div className="rfq-form-group">
                                                    <label>Phone</label>
                                                    <input
                                                        type="tel"
                                                        value={state.selectedRfq.quotedByPhone || ''}
                                                        onChange={function(e) { self.updateSelectedRfq('quotedByPhone', e.target.value); }}
                                                        placeholder="(555) 555-5555"
                                                        className="rfq-text-input"
                                                    />
                                                </div>
                                            </div>

                                            <div className="rfq-quote-actions">
                                                <button 
                                                    onClick={function() { self.saveQuote(state.selectedRfq.id); }}
                                                    className="btn-save-quote"
                                                >
                                                    Save Quote
                                                </button>
                                                <button 
                                                    onClick={function() { self.generateQuotePdf(); }}
                                                    className="btn-generate-pdf"
                                                >
                                                    Generate PDF Quote
                                                </button>
                                                <button 
                                                    onClick={function() { self.sendEmail(); }}
                                                    className="btn-send-quote"
                                                >
                                                    Email Quote to Customer
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Notes Tab */}
                                {state.activeTab === 'notes' && (
                                    <div className="rfq-tab-content">
                                        <div className="rfq-details-section">
                                            <h4>Internal Notes</h4>
                                            <textarea
                                                value={state.selectedRfq.notes || ''}
                                                onChange={function(e) { self.updateSelectedRfq('notes', e.target.value); }}
                                                placeholder="Add internal notes about this RFQ..."
                                                rows="10"
                                                className="rfq-notes-textarea"
                                            />
                                            <button 
                                                className="btn-save-notes"
                                                onClick={function() { self.saveNotes(state.selectedRfq.id, state.selectedRfq.notes); }}
                                            >
                                                Save Notes
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="rfq-modal-footer">
                                <button 
                                    className="btn-delete"
                                    onClick={function() { self.deleteRfq(state.selectedRfq.id); }}
                                >
                                    Delete RFQ
                                </button>
                                <button className="btn-close" onClick={function() { self.closeDetailsModal(); }}>
                                    Close
                                </button>
                            </div>

                            {/* Resize Handle */}
                            <div 
                                className="rfq-modal-resize-handle"
                                onMouseDown={this.handleResizeMouseDown}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default connect(
    function(store) {
        return {
            projectList: store.projectList
        };
    },
    { updateActiveProject, updateActiveTabIndex }
)(RfqManager);